import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Skeleton } from '@mui/material';
import { ModalBase } from '@components/Modal';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import { handleApiError } from '@helpers/api';
import { useTranslation } from '@hooks';
import { googleIntegrationService } from '@services';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        maxWidth: 480,
        width: 'calc(100% - 32px)',
    },
    '& .modal-base-content': {
        padding: 16,
    },
});

const CreateGoogleProfileModal = ({
    open,
    onClose,
    integrations,
    defaults,
    initialIntegrationId,
    initialAccounts,
    onCreate,
    creating,
}) => {
    const { t } = useTranslation();
    const [selectedIntegrationId, setSelectedIntegrationId] = useState(initialIntegrationId || '');
    const [accounts, setAccounts] = useState(initialAccounts || []);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [selectedGoogleAccountId, setSelectedGoogleAccountId] = useState('');

    const resolvePreselectedGroup = (groups, preferredGoogleAccountId) => {
        if (preferredGoogleAccountId && groups.some(x => x.id === preferredGoogleAccountId)) {
            return preferredGoogleAccountId;
        }
        return groups[0]?.id || '';
    };

    useEffect(() => {
        if (!open) return;
        setSelectedGoogleAccountId(resolvePreselectedGroup(initialAccounts || [], defaults?.googleAccountId));
    }, [open]);

    const loadAccounts = async integrationId => {
        if (!integrationId) {
            setAccounts([]);
            setSelectedGoogleAccountId('');
            return;
        }
        setAccountsLoading(true);
        try {
            const groups = await googleIntegrationService.getAccounts(integrationId);
            const list = Array.isArray(groups) ? groups : [];
            setAccounts(list);
            setSelectedGoogleAccountId(resolvePreselectedGroup(list, defaults?.googleAccountId));
        } catch (error) {
            handleApiError(error);
            setAccounts([]);
            setSelectedGoogleAccountId('');
        } finally {
            setAccountsLoading(false);
        }
    };

    const onIntegrationChange = async event => {
        const integrationId = event.target.value;
        setSelectedIntegrationId(integrationId);
        await loadAccounts(integrationId);
    };

    const integrationOptions = useMemo(
        () =>
            (integrations || []).map(i => ({
                value: i.id,
                label: i.accountLabel || i.userIdentifier || i.id,
            })),
        [integrations],
    );

    const accountOptions = useMemo(() => accounts.map(a => ({ value: a.id, label: a.name || a.id })), [accounts]);

    const showIntegrationSelect = (integrations || []).length > 1;
    const showAccountSelect = accounts.length > 1;

    const onConfirm = () => onCreate(selectedIntegrationId, selectedGoogleAccountId || null);

    return (
        <Modal
            isOpen={open}
            handleClose={onClose}
            title={t('SettingsCompanies.createGoogleProfileTitle')}
            onPrimaryAction={onConfirm}
            primaryActionText={t('buttons.createNewProfile')}
            primaryActionDisabled={creating || accountsLoading || !selectedIntegrationId}
        >
            <Typography variant="body2" color="textSecondary" sx={{ marginBottom: '16px' }}>
                {t('SettingsCompanies.createGoogleProfileDescription')}
            </Typography>
            {showIntegrationSelect && (
                <Select
                    label={t('SettingsCompanies.googleAccount')}
                    value={selectedIntegrationId}
                    onChange={onIntegrationChange}
                    options={integrationOptions}
                    disabled={creating}
                />
            )}
            {accountsLoading ? (
                <Skeleton variant="rounded" width="100%" height={40} />
            ) : (
                showAccountSelect && (
                    <Select
                        label={t('SettingsCompanies.googleLocationGroup')}
                        value={selectedGoogleAccountId}
                        onChange={e => setSelectedGoogleAccountId(e.target.value)}
                        options={accountOptions}
                        disabled={creating}
                    />
                )
            )}
        </Modal>
    );
};

export default CreateGoogleProfileModal;
